import React from 'react';

import { ResultOf } from '@graphql-typed-document-node/core';

import { Tabs, TabDataCard, DataCardGroup, TabDataCardDropdown } from '@repo/ui/Tabs';

import { EnabledCheckbox as Checkbox } from './EnabledCheckbox';

import DidsForCampaignTable from './DidsForCampaignTable';

import CAMPAIGN_SHOW_Q from '../gql/campaignShow.gql';
import { useAccount } from '@/hooks/useAccount';
import { Mercury } from '@repo/types/Models';
import { formatDate } from '@repo/util/formatDate';
export type CampaginViewPropsData = ResultOf<typeof CAMPAIGN_SHOW_Q>;

export interface CampaginViewProps {
  data: CampaginViewPropsData | undefined;
  error?: Error | undefined;
  loading: boolean;
}

const CampaignView = ({ data, error, loading }: CampaginViewProps) => {
  const cspCampaign = data?.cspCampaign;

  const account = useAccount();
  const accountId = account.id;

  const CampaignOverviewData: DataCardGroup[] = [
    {
      groupTitle: 'firstRow',
      columnCount: 3,
      groupData: [
        {
          label: 'Campaign ID',
          entry: cspCampaign?.tcr_campaign_id,
        },
        {
          label: 'Business Name or DBA',
          entry: cspCampaign?.brand?.displayName,
        },
        {
          label: 'Brand ID',
          entry: cspCampaign?.tcr_brand_id,
        },
        { label: 'Status', entry: cspCampaign?.active ? 'Active' : 'Inactive' },
        {
          label: 'Registration date',
          entry: cspCampaign?.tcrCreatedAt && formatDate(cspCampaign?.tcrCreatedAt),
        },
      ],
    },

    {
      groupTitle: 'secondRow',
      columnCount: 3,
      groupData: [
        {
          label: 'Use case',
          entry: cspCampaign?.usecase,
        },
        {
          label: 'Sub use cases',
          entry: cspCampaign?.sub_usecases
            ? Object?.values(cspCampaign?.sub_usecases).join(', ')
            : ' ',
        },

        {
          label: 'MNOs',
          entry: cspCampaign?.mnos
            .map((mno: Mercury.Models.CspCampaignMno) => {
              return mno.mno;
            })
            .join(', '),
        },
      ],
    },
  ];

  const CampaignData: DataCardGroup[] = [
    {
      groupTitle: 'Message flow',
      columnCount: 2,
      groupData: [
        {
          label: 'Subscriber opt-in',
          entry: (
            <>
              <Dropdown
                checked={cspCampaign?.subscriber_optin}
                label="Keywords and messages"
                items={[
                  {
                    label: 'Opt-in keywords',
                    entry: cspCampaign?.optin_keywords,
                  },
                  {
                    label: 'Opt-in message',
                    entry: cspCampaign?.optin_message,
                  },
                ]}
              />
            </>
          ),
        },
        {
          label: 'Subscriber opt-out',
          entry: (
            <>
              <Dropdown
                checked={cspCampaign?.subscriber_optout}
                label="Keywords and messages"
                items={[
                  {
                    label: 'Opt-out keywords',
                    entry: cspCampaign?.optout_keywords,
                  },
                  {
                    label: 'Opt-out message',
                    entry: cspCampaign?.optout_message,
                  },
                ]}
              />
            </>
          ),
        },
        {
          label: 'Subscriber help',
          entry: (
            <>
              <Dropdown
                checked={cspCampaign?.subscriber_help}
                label="Keywords and messages"
                items={[
                  {
                    label: 'Help keywords',
                    entry: cspCampaign?.help_keywords,
                  },
                  {
                    label: 'Help message',
                    entry: cspCampaign?.help_message,
                  },
                ]}
              />
            </>
          ),
        },
      ],
    },
    {
      groupTitle: 'Embedded settings',
      columnCount: 3,
      groupData: [
        {
          label: 'Embedded link',
          entry: cspCampaign?.embedded_link_sample ? (
            <Dropdown
              checked={cspCampaign?.embedded_link}
              label="Sample"
              items={[
                {
                  entry: cspCampaign?.embedded_link_sample,
                },
              ]}
            />
          ) : (
            <div className="mt-4">
              <Checkbox
                checked={cspCampaign?.embedded_link}
                label={cspCampaign?.embedded_link ? 'Enabled' : 'Disabled'}
                disabled={!cspCampaign?.embedded_link}
              />
            </div>
          ),
        },
        {
          label: 'Embedded phone number',
          entry: (
            <div className="mt-4">
              <Checkbox
                checked={cspCampaign?.embedded_phone}
                label={cspCampaign?.embedded_phone ? 'Enabled' : 'Disabled'}
                disabled={!cspCampaign?.embedded_phone}
              />
            </div>
          ),
        },
      ],
    },
    {
      groupTitle: 'Other settings',
      columnCount: 3,
      groupData: [
        {
          label: 'Age gated',
          entry: (
            <div className="mt-4">
              <Checkbox
                checked={cspCampaign?.age_gated}
                label={cspCampaign?.age_gated ? 'Enabled' : 'Disabled'}
                disabled={!cspCampaign?.age_gated}
              />
            </div>
          ),
        },
        {
          label: 'Direct lending',
          entry: (
            <div className="mt-4">
              <Checkbox
                checked={cspCampaign?.direct_lending}
                label={cspCampaign?.direct_lending ? 'Enabled' : 'Disabled'}
                disabled={!cspCampaign?.direct_lending}
              />
            </div>
          ),
        },
        {
          label: 'Affiliate marketing',
          entry: (
            <div className="mt-4">
              <Checkbox
                checked={cspCampaign?.affiliate_marketing}
                label={cspCampaign?.direct_lending ? 'Enabled' : 'Disabled'}
                disabled={!cspCampaign?.affiliate_marketing}
              />
            </div>
          ),
        },
        {
          label: 'Terms and conditions',
          entry: (
            <div className="flex flex-col gap-4 mt-2">
              <p className="text-sm">
                I confirm that this campaign will not be used for Affiliate Marketing.
              </p>
              <Checkbox
                checked={cspCampaign?.terms_and_conditions}
                label={cspCampaign?.terms_and_conditions ? 'Accepted' : 'Not accepted'}
                disabled={!cspCampaign?.terms_and_conditions}
              />
            </div>
          ),
        },
      ],
    },
  ];

  const CampaignDetailsData: DataCardGroup[] = [
    {
      groupTitle: 'Campaign and content attributes',
      columnCount: 2,
      groupData: [
        {
          label: 'Campaign description',
          entry: cspCampaign?.description,
        },
        {
          label: 'Call-to-action / Message flow',
          entry: cspCampaign?.message_flow,
        },
        {
          label: 'Privacy policy link',
          entry: cspCampaign?.privacy_policy_link ? cspCampaign?.privacy_policy_link : 'N/A',
        },
        {
          label: 'Terms and conditions link',
          entry: cspCampaign?.terms_and_conditions_link
            ? cspCampaign?.terms_and_conditions_link
            : 'N/A',
        },
      ],
    },
  ];

  const SampleMessageData: DataCardGroup[] = [
    {
      groupTitle: 'Sample messages',
      columnCount: 1,
      groupData: [
        {
          label: '',
          entry: (
            <ol className="flex flex-col gap-4 ml-5 list-decimal">
              {cspCampaign?.samples.map((sample) => <li key={sample}>{sample}</li>)}
            </ol>
          ),
        },
      ],
    },
  ];

  const CampaignTabs = [
    {
      title: 'Overview',
      contents: (
        <>
          <TabDataCard
            title="Campaign overview"
            icon="corporate_fare"
            loading={loading}
            data={CampaignOverviewData}
          />
          <TabDataCard
            title="Campaign and content attributes"
            icon="list_alt"
            loading={loading}
            data={CampaignData}
          />
          <TabDataCard
            title="Campaign details"
            icon="campaign"
            loading={loading}
            data={CampaignDetailsData}
          />
          <TabDataCard
            title="Sample messages"
            icon="sms"
            loading={loading}
            data={SampleMessageData}
          />
        </>
      ),
    },

    {
      title: 'Numbers',
      contents: <DidsForCampaignTable accountId={accountId} cspCampaignId={cspCampaign?.id} />,
    },
  ];

  return <Tabs tabs={CampaignTabs} loading={loading} error={error} />;
};

export default CampaignView;

interface DropdownProps {
  checked: boolean | undefined;
  label: string;
  items: {
    label?: string;
    entry: string | undefined;
  }[];
}

const Dropdown = ({ checked, items, label }: DropdownProps) => {
  return (
    <div className="flex flex-col gap-4 mt-4">
      <Checkbox checked={checked} />
      <TabDataCardDropdown label={label}>
        <div className="flex flex-col gap-4 w-full">
          {items.map((item) =>
            item.label ? (
              <div key={item.label} className="grid grid-cols-[30%_1fr] gap-2">
                <span>{item.label}: </span>
                <span>{item.entry}</span>
              </div>
            ) : (
              <span>{item.entry}</span>
            )
          )}
        </div>
      </TabDataCardDropdown>
    </div>
  );
};
